import { useState, useRef, useCallback, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import { EventClickArg } from '@fullcalendar/core';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import rrulePlugin from '@fullcalendar/rrule';
import {
  Box,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  SnackbarCloseReason,
} from '@mui/material';
import './CalendarView.scss';
import useGetEventsByRange from '@src/hooks/api/queries/modes/useGetEventsByRange';
import moment from 'moment';
import EventPopover from './EventPopover';
import EventEditorWindow from './EventEditorWindow';
import { CalendarEvent, PopoverEvent } from '@src/types/Events';
import { UUID } from 'crypto';
import useGetStacks from '@src/hooks/api/queries/modes/useGetStacks';
import { Stack as FracStack } from '@src/types/Stacks';
import CalendarHeader from './CalendarHeader';
import EventTile from './EventTile';
import EventDeleteDialog from './EventDeleteDialog';

export default function CalendarView() {
  const calendarRef = useRef<FullCalendar>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activeEvent, setActiveEvent] = useState<CalendarEvent | null>(null);
  const [popoverEvent, setPopoverEvent] = useState<PopoverEvent | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [range, setRange] = useState<{
    from: string | null;
    to: string | null;
  }>({
    from: null,
    to: null,
  });
  const [allEvents, setAllEvents] = useState<CalendarEvent[]>([]);
  const { data: events } = useGetEventsByRange({
    body: {
      from: range?.from,
      to: range?.to,
    },
  });

  useEffect(() => {
    if (events && Array.isArray(events)) {
      setAllEvents((prev) => {
        const existingIds = new Set(prev.map((e) => e.id));
        const newEvents = events.filter((e) => !existingIds.has(e.id));
        return [...prev, ...newEvents];
      });
    }
  }, [events]);

  const {
    data: stacksListData,
    isFetching: isStackFetching,
    isRefetching: isStackRefetching,
  } = useGetStacks();

  const stacksList = stacksListData || ([] as FracStack[]);

  const handleDateClick = (dateClickInfo: DateClickArg) => {
    setActiveEvent({
      title: '',
      start: moment.utc(dateClickInfo.date).toISOString(),
      end: moment.utc(dateClickInfo.date).toISOString(),
      backgroundColor: '#5FC8F1',
      stackId: '' as UUID,
      startCommands: [],
      stopCommands: [],
    });
    setDialogOpen(true);
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    const event = events?.find((e) => e.id === clickInfo.event.id);
    const range = clickInfo.event._instance?.range;
    if (!event) {
      return;
    }

    setPopoverEvent({
      id: event.id as UUID,
      title: event.title,
      start: moment.utc(range?.start).toISOString(),
      end: moment.utc(range?.end).toISOString(),
      recurrenceFreq: event.rrule?.freq,
      backgroundColor: event.backgroundColor,
      startCommands: event.startCommands,
      stopCommands: event.stopCommands,
    });
    setActiveEvent({
      id: event.id as UUID,
      title: event.title,
      start: moment.utc(event.start).toISOString(),
      end: moment.utc(event.end).toISOString(),
      ...(event.rrule
        ? {
            rrule: event.rrule,
          }
        : {}),
      backgroundColor: event.backgroundColor || '',
      stackId: event.stackId || '',
      startCommands: event.startCommands || [],
      stopCommands: event.stopCommands || [],
    });
    setAnchorEl(clickInfo.el);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleEditEvent = () => {
    setDialogOpen(true);
    handlePopoverClose();
  };

  const handleAddEvent = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleClose = (
    _: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setConfirmOpen(false);
  };

  const handleDatesSet = useCallback((dateInfo: { start: Date; end: Date }) => {
    const newFrom = moment(dateInfo.start).toISOString();
    const newTo = moment(dateInfo.end).toISOString();

    setRange({ from: newFrom, to: newTo });
  }, []);

  if (isStackFetching || isStackRefetching) {
    return <CircularProgress />;
  }

  return (
    <Box sx={{ height: 'calc(100vh - 129px)', my: 2, mx: 0.5 }}>
      <Snackbar
        open={confirmOpen}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleClose}
          severity={error ? 'error' : 'success'}
          variant='filled'
        >
          {error ? error : 'Event updated successfully!'}
        </Alert>
      </Snackbar>
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          borderRadius: 0,
          overflow: 'hidden',
          bgcolor: 'rgb(48 51 57)',
        }}
      >
        <CalendarHeader onAddEvent={handleAddEvent} calendarRef={calendarRef} />

        <Box sx={{ flexGrow: 1, px: 3, pb: 3 }}>
          <FullCalendar
            ref={calendarRef}
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              interactionPlugin,
              momentTimezonePlugin,
              rrulePlugin,
            ]}
            initialView='timeGridWeek'
            events={allEvents}
            headerToolbar={{
              left: '',
              center: '',
              right: '',
            }}
            selectable={true}
            selectMirror={true}
            dayMaxEvents={true}
            weekends={true}
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            datesSet={handleDatesSet}
            timeZone='UTC'
            eventTimeFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short',
              hour12: true,
            }}
            height='100%'
            themeSystem='standard'
            eventContent={EventTile}
            eventDidMount={(info) => {
              info.el.style.backgroundColor = info.event.backgroundColor;
            }}
          />
        </Box>
      </Paper>

      <EventPopover
        anchorEl={anchorEl}
        onEdit={handleEditEvent}
        onClose={handlePopoverClose}
        popoverEvent={popoverEvent}
        setOpen={setDeleteDialogOpen}
      />

      <EventEditorWindow
        dialogOpen={dialogOpen}
        activeEvent={activeEvent as CalendarEvent}
        stacksList={stacksList}
        onClose={handleDialogClose}
        setError={setError}
        setDialogOpen={setDialogOpen}
        setConfirmOpen={setConfirmOpen}
        setAllEvents={setAllEvents}
      />

      {deleteDialogOpen && activeEvent?.id && (
        <EventDeleteDialog
          setOpen={setDeleteDialogOpen}
          eventId={activeEvent?.id}
          setAnchorEl={setAnchorEl}
        />
      )}
    </Box>
  );
}
